// Permission system based on Keycloak realm roles and groups.
//
// Keycloak JWT structure expected:
// {
//   "realm_access": { "roles": ["operator"] },
//   "groups": ["/tenant-kimeradev"],    // requires Group Membership mapper
//   "preferred_username": "john.doe"
// }
//
// Keycloak setup required:
//   1. Realm roles: platform-admin, tenant-admin, operator, viewer, provisioner
//   2. "groups" claim: add a "Group Membership" mapper in Keycloak client
//      - Token Claim Name: groups
//      - Full group path: true

import type { User } from 'oidc-client-ts'

// Keycloak-specific claims added to the JWT by Keycloak mappers.
// UserProfile in oidc-client-ts v3 is a type alias (IdTokenClaims), not an interface,
// so we use a local cast type instead of module augmentation.
interface KeycloakProfile {
  realm_access?: { roles: string[] }
  resource_access?: Record<string, { roles: string[] }>
  groups?: string[]
}

// ---- Platform roles --------------------------------------------------------

export type PlatformRole =
  | 'platform-admin'
  | 'tenant-admin'
  | 'operator'
  | 'viewer'
  | 'provisioner'

// ---- Granular permissions --------------------------------------------------

export type Permission =
  // Devices
  | 'devices:read'
  | 'devices:write'
  | 'devices:delete'
  | 'devices:provision'
  // Fleets
  | 'fleets:read'
  | 'fleets:write'
  | 'fleets:delete'
  // Operations (OTA rollouts, playbooks)
  | 'operations:read'
  | 'operations:execute'
  // Device types
  | 'device-types:read'
  | 'device-types:write'
  // Tenants (platform admin only)
  | 'tenants:read'
  | 'tenants:write'
  // Platform admin access
  | 'admin:access'

// ---- Role → permission mapping --------------------------------------------

const ROLE_PERMISSIONS: Record<PlatformRole, Permission[]> = {
  'platform-admin': [
    'admin:access',
    'tenants:read',
    'tenants:write',
    'devices:read',
    'devices:write',
    'devices:delete',
    'devices:provision',
    'fleets:read',
    'fleets:write',
    'fleets:delete',
    'operations:read',
    'operations:execute',
    'device-types:read',
    'device-types:write',
  ],
  'tenant-admin': [
    'tenants:read',
    'devices:read',
    'devices:write',
    'devices:delete',
    'devices:provision',
    'fleets:read',
    'fleets:write',
    'fleets:delete',
    'operations:read',
    'operations:execute',
    'device-types:read',
  ],
  operator: [
    'devices:read',
    'devices:write',
    'fleets:read',
    'fleets:write',
    'operations:read',
    'operations:execute',
    'device-types:read',
  ],
  viewer: [
    'devices:read',
    'fleets:read',
    'operations:read',
    'device-types:read',
  ],
  provisioner: [
    'devices:read',
    'devices:provision',
    'fleets:read',
    'device-types:read',
  ],
}

// ---- Derived helpers -------------------------------------------------------

/** Extract platform roles from a Keycloak JWT. */
export function getRoles(user: User): PlatformRole[] {
  const profile = user.profile as KeycloakProfile
  // realm_access may come from userinfo or from the ID token claims directly.
  // oidc-client-ts merges ID token claims into profile, but Keycloak sometimes
  // only includes realm_access in the access token — so we also parse it manually.
  const fromProfile = profile.realm_access?.roles ?? []

  // Parse access token claims as fallback (access token is a JWT)
  let fromAccessToken: string[] = []
  try {
    const payload = user.access_token?.split('.')[1]
    if (payload) {
      const decoded = JSON.parse(atob(payload)) as KeycloakProfile
      fromAccessToken = decoded.realm_access?.roles ?? []
    }
  } catch {
    // ignore parse errors
  }

  const merged = Array.from(new Set([...fromProfile, ...fromAccessToken]))
  return merged.filter((r): r is PlatformRole => r in ROLE_PERMISSIONS)
}

/** Return all permissions granted to the user based on their roles. */
export function getPermissions(user: User): Set<Permission> {
  const roles = getRoles(user)
  const perms = new Set<Permission>()
  for (const role of roles) {
    for (const perm of ROLE_PERMISSIONS[role]) {
      perms.add(perm)
    }
  }
  return perms
}

/** Check if the user has a specific permission. */
export function hasPermission(user: User | null | undefined, perm: Permission): boolean {
  if (!user) return false
  return getPermissions(user).has(perm)
}

// ---- Tenant / group helpers ------------------------------------------------

// Keycloak groups for tenant membership follow the pattern /tenant-{slug}
const TENANT_PREFIX = '/tenant-'

/** Extract tenant slugs from the user's Keycloak groups. */
export function getTenantSlugs(user: User): string[] {
  const profile = user.profile as KeycloakProfile
  const groups = profile.groups ?? []
  return groups
    .filter(g => g.startsWith(TENANT_PREFIX))
    .map(g => g.slice(TENANT_PREFIX.length))
}

/** Return true if the user is a member of the given tenant (by slug). */
export function isTenantMember(user: User, slug: string): boolean {
  if (getRoles(user).includes('platform-admin')) return true
  return getTenantSlugs(user).includes(slug)
}
